import { RadioButton } from "primereact/radiobutton";
import React from "react";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import 'primeicons/primeicons.css';
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import { perPageOptionsFor } from "./perPageOptions.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The per-page count radio group.
 *
 * Which counts a format offers lives in `perPageOptions.ts`, not here: the
 * page-count measurement script sweeps every format at every offered count
 * against the real renderer, and it has to ask the same question this panel
 * asks. Two copies of that table would drift, silently and in the worst
 * direction. See the header on `perPageOptions.ts`.
 */
export const NumberOfProblems = React.memo(function NumberOfProblems() {
    const {getSettings, setSettings} = useSettingsActions();
    // The count is already within what the sheet offers: whoever owns the
    // settings clamps them (`withPerPageClamped`, ADR 0082), not this control.
    const {options, current} = useSetting((settings) => ({
        options: perPageOptionsFor(settings),
        current: settings.NumberOfProblemsPerPage,
    }));
    const t = useTranslation();

    function handleNumberOfProblemsSelect(e) {
        let updatedSettings = {...getSettings()};
        updatedSettings.NumberOfProblemsPerPage = Number(e.target.value);
        setSettings(updatedSettings);
    }

    const radioButtonList: React.JSX.Element[] = options.map((option) => {
        const inputId = `problemsPerPage-${option}`;
        return (
            <div key={option}>
                <RadioButton
                    inputId={inputId}
                    value={option}
                    checked={current === option}
                    onChange={handleNumberOfProblemsSelect}
                />
                <label htmlFor={inputId}>{option}</label>
            </div>
        );
    });

    const radioButtons = (
        <div className='radioButtons' role='radiogroup' aria-label={t('settings.problemsPerPageAria')}>
            {radioButtonList}
        </div>
    );
    return radioButtons;
});
